'use client';

import type { Ref, ReactNode } from 'react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { useGuideSearchModel } from '../../hooks/model/use-guide-search-model';
import { useGuideSearchPickHandler } from '../../hooks/handler/use-guide-search-handler';
import { GUIDE_SEARCH_EMPTY, GUIDE_SEARCH_LABEL, GUIDE_SEARCH_PLACEHOLDER } from '../../constants/guide-copy';

import type { GuideSearchItem } from '../../utils/guide-search';
import type { GuideSearchModel } from '../../hooks/model/use-guide-search-model';

type GuideSearchFieldProps = {
  search: GuideSearchModel;
  /** 결과 하나를 골랐을 때 — `useGuideSearchPickHandler` */
  onPick: (item: GuideSearchItem | null) => void;
  /** `hero` 는 홈 첫 카드의 큰 칸(높이 56 · 오른쪽 끝 `endAction`) */
  variant?: 'bar' | 'hero';
  placeholder?: string;
  /** 칸 오른쪽 끝에 서는 단추(예: 「검색」) */
  endAction?: ReactNode;
  autoFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
};

/**
 * 가이드 검색칸 — 매뉴얼 제목·부제, 하고 싶은 일, 본문 제목에서 찾아 고르면 그리로 간다.
 * 찾는 말이 있는 동안 결과 줄이 칸 아래 펼쳐진다. 위아래 화살표로 줄을 옮기고 Enter 로 고른다(MUI `Autocomplete`).
 * 찾는 말·결과는 부르는 쪽이 쥔다(`useGuideSearchModel`) — 머리 띠는 `GuideSearchBox`, 홈 첫 카드는 제 모델을 넘긴다.
 */
export function GuideSearchField({
  search,
  onPick,
  variant = 'bar',
  placeholder = GUIDE_SEARCH_PLACEHOLDER,
  endAction,
  autoFocus,
  inputRef,
}: GuideSearchFieldProps) {
  const { query, setQuery, results, touch } = search;
  const hero = variant === 'hero';

  return (
    <Autocomplete<GuideSearchItem, false, false, false>
      options={results}
      value={null}
      inputValue={query}
      onInputChange={(_event, value, reason) => {
        if (reason !== 'reset') setQuery(value);
      }}
      onChange={(_event, item) => onPick(item)}
      onOpen={touch}
      onFocus={touch}
      open={query.trim().length > 0}
      filterOptions={(options) => options}
      getOptionLabel={(item) => item.label}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      noOptionsText={GUIDE_SEARCH_EMPTY}
      popupIcon={null}
      clearIcon={null}
      fullWidth
      data-guide-part={hero ? 'hero-search' : 'search'}
      renderOption={({ key, ...props }, item) => (
        <Box component="li" key={key} {...props} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Iconify
            icon={item.kind === 'manual' ? 'solar:book-linear' : 'solar:list-linear'}
            width={18}
            sx={{ mt: 0.5, flexShrink: 0, color: 'text.secondary' }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="uiStrong" sx={{ wordBreak: 'keep-all' }}>
              {item.label}
            </Typography>
            <Typography variant="uiMeta" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {item.hint}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          inputRef={inputRef}
          placeholder={placeholder}
          size={hero ? 'medium' : 'small'}
          slotProps={{
            htmlInput: { ...params.inputProps, 'aria-label': GUIDE_SEARCH_LABEL },
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="solar:magnifer-linear" width={hero ? 22 : 18} sx={{ color: hero ? 'text.secondary' : 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: endAction ? <InputAdornment position="end">{endAction}</InputAdornment> : null,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': hero
              ? { borderRadius: RADIUS.lg, bgcolor: 'background.paper', minHeight: 56, pl: 2, pr: 1, typography: 'docBody' }
              : { borderRadius: RADIUS.pill, bgcolor: 'background.paper' },
          }}
        />
      )}
    />
  );
}

/** 머리 띠의 가이드 검색칸 — 제 찾는 말을 쥐고, 고르면 칸을 비운다 */
export function GuideSearchBox({ autoFocus }: { autoFocus?: boolean }) {
  const search = useGuideSearchModel();
  const pick = useGuideSearchPickHandler(() => search.setQuery(''));
  return <GuideSearchField search={search} onPick={pick} autoFocus={autoFocus} />;
}
